import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import AppShell from "@/components/AppShell";

import Login from "@/pages/Login";
import EmployeeDashboard from "@/pages/employee/EmployeeDashboard";
import Catalog from "@/pages/employee/Catalog";
import CurrentOrder from "@/pages/employee/CurrentOrder";
import MyOrders from "@/pages/employee/MyOrders";
import CustomProductRequestPage from "@/pages/employee/CustomProductRequestPage";
import BudgetIncreaseRequestPage from "@/pages/employee/BudgetIncreaseRequestPage";
import Suggestions from "@/pages/employee/Suggestions";

import ManagerDashboard from "@/pages/manager/ManagerDashboard";
import OrdersToApprove from "@/pages/manager/OrdersToApprove";
import ManagerHistory from "@/pages/manager/ManagerHistory";
import ManagerBudgetRequests from "@/pages/manager/ManagerBudgetRequests";
import ManagerCustomRequests from "@/pages/manager/ManagerCustomRequests";
import ManagerReports from "@/pages/manager/ManagerReports";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminApprovedOrders from "@/pages/admin/AdminApprovedOrders";
import AdminSites from "@/pages/admin/AdminSites";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminRules from "@/pages/admin/AdminRules";
import AdminCustomRequests from "@/pages/admin/AdminCustomRequests";
import AdminBudgetRequests from "@/pages/admin/AdminBudgetRequests";
import AdminCleanup from "@/pages/admin/AdminCleanup";

import OrderDetail from "@/pages/OrderDetail";

function RoleRoot() {
  const { user } = useAuth();
  if (user === undefined) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "employee") return <EmployeeDashboard />;
  if (user.role === "manager") return <ManagerDashboard />;
  return <AdminDashboard />;
}

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-slate-500 text-sm">Ładowanie…</div>
    </div>
  );
}

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === undefined) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return <AppShell>{children}</AppShell>;
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route path="/" element={<Protected><RoleRoot /></Protected>} />

            {/* Employee */}
            <Route path="/catalog" element={<Protected roles={["employee"]}><Catalog /></Protected>} />
            <Route path="/cart" element={<Protected roles={["employee"]}><CurrentOrder /></Protected>} />
            <Route path="/my-orders" element={<Protected roles={["employee"]}><MyOrders /></Protected>} />
            <Route path="/suggestions" element={<Protected roles={["employee"]}><Suggestions /></Protected>} />
            <Route path="/custom-request" element={<Protected roles={["employee"]}><CustomProductRequestPage /></Protected>} />
            <Route path="/budget-request" element={<Protected roles={["employee"]}><BudgetIncreaseRequestPage /></Protected>} />

            {/* Manager */}
            <Route path="/manager/orders" element={<Protected roles={["manager"]}><OrdersToApprove /></Protected>} />
            <Route path="/manager/history" element={<Protected roles={["manager"]}><ManagerHistory /></Protected>} />
            <Route path="/manager/reports" element={<Protected roles={["manager"]}><ManagerReports /></Protected>} />
            <Route path="/manager/budget-requests" element={<Protected roles={["manager"]}><ManagerBudgetRequests /></Protected>} />
            <Route path="/manager/custom-requests" element={<Protected roles={["manager"]}><ManagerCustomRequests /></Protected>} />

            {/* Admin */}
            <Route path="/admin/orders" element={<Protected roles={["office_land_admin"]}><AdminApprovedOrders /></Protected>} />
            <Route path="/admin/sites" element={<Protected roles={["office_land_admin"]}><AdminSites /></Protected>} />
            <Route path="/admin/users" element={<Protected roles={["office_land_admin"]}><AdminUsers /></Protected>} />
            <Route path="/admin/products" element={<Protected roles={["office_land_admin"]}><AdminProducts /></Protected>} />
            <Route path="/admin/rules" element={<Protected roles={["office_land_admin"]}><AdminRules /></Protected>} />
            <Route path="/admin/custom-requests" element={<Protected roles={["office_land_admin"]}><AdminCustomRequests /></Protected>} />
            <Route path="/admin/budget-requests" element={<Protected roles={["office_land_admin"]}><AdminBudgetRequests /></Protected>} />
            <Route path="/admin/cleanup" element={<Protected roles={["office_land_admin"]}><AdminCleanup /></Protected>} />

            {/* Shared */}
            <Route path="/orders/:orderId" element={<Protected><OrderDetail /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
